import { apiClient } from '@/lib/api-client'
import type { InternalJobPosting } from '@/services/portal.service'

const BASE = '/api/v1/portal/internal-mobility'

export type MobilityDocumentType = 'resume' | 'performance_review'

export interface MobilityDocument {
  id: number
  document_type: MobilityDocumentType
  original_filename: string
  mime_type: string
  byte_size: number
  status: 'uploading' | 'processing' | 'ready' | 'failed' | 'replaced' | 'deleted'
  error_code: string | null
  source_page_count: number | null
  created_at: string
  updated_at: string
}

export interface CandidateUploadProgress {
  phase: 'creating' | 'uploading' | 'processing'
  percent: number
  documentId?: number
}

export interface MobilityCandidate {
  id: number
  name: string
  email: string | null
  current_role: string | null
  department: string | null
  resume: MobilityDocument | null
  performance_reviews: MobilityDocument[]
  readiness: 'ready' | 'not_ready'
  readiness_reason_codes: string[]
}

export interface MobilityCandidateList {
  candidates: MobilityCandidate[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface ChartNode {
  id: string
  node_type: 'organization' | 'department' | 'person'
  employee_user_id: number | null
  role_id: number | null
  department_id: string | null
  parent_node_id: string | null
  display_name: string
  role_title: string
  department_name: string
  level: string | null
  extracted_values: Record<string, unknown>
  source_page: number | null
  confidence: Record<string, number>
  review_status: 'unreviewed' | 'needs_review' | 'reviewed'
  review_issue_codes: string[]
  position_x: number | null
  position_y: number | null
}

export interface OrganizationChart {
  id: string
  status: 'uploading' | 'processing' | 'review_required' | 'ready' | 'failed' | 'superseded'
  original_filename: string
  source_page_count: number | null
  revision: number
  error_code: string | null
  published_at: string | null
  created_at: string
  nodes: ChartNode[]
}

export interface MobilityDepartment {
  id: string
  name: string
  aliases: string[]
  is_active: boolean
}

export interface MatrixScoreComponents {
  semantic_similarity?: number | null
  skill_coverage?: number | null
  seniority_alignment?: number | null
  match_confidence?: string | null
  scoring_version?: string | null
  [key: string]: number | string | null | undefined
}

export interface MatrixRanking {
  id: string
  internal_job_id: number
  rank: number
  match_score: number
  score_components: MatrixScoreComponents
  reason_codes: string[]
  evidence_references: {
    document_id: number
    document_type?: MobilityDocumentType
    chunk_ids?: string[]
    source_pages?: number[]
  }[]
  explanation: string | null
  job: {
    id: number
    title: string
    department: string | null
    location: string | null
    job_type: string | null
    skills: string[]
    matched_skills?: string[]
    partial_skills?: string[]
    missing_skills?: string[]
    supporting_skills?: Record<string, string[]>
    match_confidence?: 'none' | 'partial' | 'full'
  }
}

export interface EnhancedMobilitySkill {
  name: string
  aliases?: string[]
  confidence?: number
  evidence_strength?: 'weak' | 'medium' | 'strong'
  source_count?: number
  source_types?: MobilityDocumentType[]
  last_demonstrated?: string | null
  years_experience?: number | null
  is_current_role?: boolean
  evidence: Record<string, unknown>[]
}

export interface EnhancedMobilityProfile {
  profile_id: number
  status: string
  generation_version: string
  current_role: string | null
  current_department: string | null
  skills: EnhancedMobilitySkill[]
  accomplishments: { value: string; evidence: Record<string, unknown>[] }[]
  responsibilities: { value: string; evidence: Record<string, unknown>[] }[]
  experience: { value: string; evidence: Record<string, unknown>[] }[]
  qualifications: { value: string; evidence: Record<string, unknown>[] }[]
  domain_context: { value: string; evidence: Record<string, unknown>[] }[]
  source_document_ids: number[]
  evidence_count: number
  updated_at: string
}

export interface MatrixCandidateResult {
  candidate: MobilityCandidate
  result_status:
    | 'never_generated'
    | 'up_to_date'
    | 'needs_regeneration'
    | 'processing'
    | 'failed'
    | 'skipped'
  regeneration_status: string | null
  generated_at: string | null
  reasoning_status: string | null
  skills: EnhancedMobilitySkill[]
  enhanced_profile: EnhancedMobilityProfile | null
  rankings: MatrixRanking[]
  annotation: { review_status: string; private_notes: string }
}

export interface MatrixCandidateList {
  candidates: MatrixCandidateResult[]
  total: number
  page: number
  limit: number
  pages: number
  open_job_count?: number
  mapped_open_job_count?: number
  unmapped_open_job_count?: number
}

export interface DepartmentNeedCandidateMatch {
  candidate_user_id: number
  name: string
  email: string | null
  current_role: string | null
  current_department: string | null
  match_score: number
  score_components: MatrixScoreComponents
  reason_codes: string[]
  evidence_references: MatrixRanking['evidence_references']
  explanation: string | null
  matched_skills: string[]
  partial_skills: string[]
  missing_skills: string[]
  generated_at: string | null
  reasoning_status: string
}

export interface DepartmentNeedCandidateList {
  department: MobilityDepartment
  job: InternalJobPosting
  matches: DepartmentNeedCandidateMatch[]
  total: number
  page: number
  limit: number
  pages: number
  enrolled_candidate_count: number
  ranking_ready_candidate_count: number
  generated_candidate_count: number
  awaiting_generation_count: number
  latest_matrix_status: string | null
}

export interface InternalMobilityReadiness {
  chart_status: string
  structure_ready: boolean
  department_count: number
  open_role_count: number
  mapped_open_role_count: number
  unmapped_open_role_count: number
  people_count: number
  enrolled_candidate_count: number
  ranking_ready_candidate_count: number
  ready_resume_count: number
  ready_performance_review_count: number
  missing_resume_count: number
  missing_performance_review_count: number
  documents_processing_count: number
  documents_failed_count: number
  latest_matrix_status: string | null
  latest_matrix_completed_at: string | null
}

interface DepartmentNeedsOverview {
  setup: InternalMobilityReadiness
}

export interface DepartmentTalentCoverageDepartment {
  id: string
  name: string
  first_role_id: number
  open_role_count: number
  strong_candidate_count: number
  covered_role_count: number
  bench_role_count: number
  coverage_status: 'healthy' | 'partial' | 'none'
}

export interface DepartmentTalentMatrixCell {
  department_id: string
  best_role_id: number | null
  best_role_title: string | null
  best_match_score: number | null
  strong_role_count: number
  classification: DepartmentTalentMatrixClassification
}

export type DepartmentTalentMatrixClassification =
  | 'strong'
  | 'potential'
  | 'development'
  | 'stale'
  | 'no_result'

export interface DepartmentTalentMatrixCandidate {
  candidate_user_id: number
  name: string
  current_role: string | null
  current_department: string | null
  result_status: MatrixCandidateResult['result_status']
  cells: DepartmentTalentMatrixCell[]
}

export interface DepartmentTalentCoverage {
  strong_match_threshold: number
  summary: {
    open_role_count: number
    covered_role_count: number
    uncovered_role_count: number
    strong_candidate_count: number
    enrolled_candidate_count: number
    ranking_ready_candidate_count: number
    generated_candidate_count: number
    current_candidate_count: number
    stale_candidate_count: number
    latest_matrix_status: string | null
    latest_matrix_completed_at: string | null
  }
  departments: DepartmentTalentCoverageDepartment[]
  department_total: number
  department_page: number
  department_limit: number
  department_pages: number
  candidates: DepartmentTalentMatrixCandidate[]
  candidate_total: number
  candidate_page: number
  candidate_limit: number
  candidate_pages: number
}

export type DepartmentTalentCoverageFilter =
  | 'none'
  | 'partial'
  | 'healthy'
  | 'covered'
  | 'with_strong_candidates'
  | 'with_uncovered_roles'

export interface MobilityFunnelStage {
  key: 'people' | 'enrolled' | 'evidence_ready' | 'generated' | 'current' | 'strong_match'
  label: string
  count: number
  remedy: string | null
}

export type RoleOpportunityQuadrant =
  | 'fill_internally'
  | 'train_and_fill'
  | 'verify_match'
  | 'hire_externally'
  | 'no_signal'

export interface RoleOpportunity {
  job_id: number
  title: string
  department_id: string
  department_name: string
  best_match_score: number | null
  requirement_coverage: number | null
  strong_candidate_count: number
  scored_candidate_count: number
  quadrant: RoleOpportunityQuadrant
}

export interface TalentFlowEdge {
  source_department_id: string | null
  source_department_name: string
  target_department_id: string
  target_department_name: string
  candidate_count: number
}

export interface DepartmentFlowBalance {
  department_id: string
  name: string
  inbound_count: number
  outbound_count: number
  internal_count: number
  net_count: number
}

export interface DepartmentNeedsInsights {
  strong_match_threshold: number
  trainable_coverage_threshold: number
  latest_matrix_status: string | null
  latest_matrix_completed_at: string | null
  funnel: MobilityFunnelStage[]
  roles: RoleOpportunity[]
  flows: TalentFlowEdge[]
  flow_balances: DepartmentFlowBalance[]
}

export interface CandidateEvidence {
  candidate_user_id: number
  name: string
  department: string | null
  has_resume: boolean
  has_performance_review: boolean
  skill_fact_count: number
  supporting_fact_count: number
  unsupported_match_count: number
  scored_match_count: number
  quality: 'strong' | 'thin' | 'none'
  issue: 'unreadable_document' | 'missing_document' | 'partial_evidence' | 'none'
  remedy: string | null
}

export interface EvidenceQuality {
  summary: {
    candidate_count: number
    strong_count: number
    thin_count: number
    none_count: number
    skill_fact_total: number
    unsupported_match_count: number
    scored_match_count: number
    thin_skill_threshold: number
  }
  candidates: CandidateEvidence[]
}

export interface MatrixRun {
  id: string
  status: 'queued' | 'running' | 'partial' | 'complete' | 'failed' | 'cancelled'
  candidate_count: number
  eligible_count: number
  completed_count: number
  failed_count: number
  skipped_count: number
  created_at: string
  completed_at: string | null
}

export async function getMobilityCandidates(params: {
  page: number
  limit: number
  search?: string
  readiness?: 'ready' | 'not_ready'
}): Promise<MobilityCandidateList> {
  const { data } = await apiClient.get<MobilityCandidateList>(`${BASE}/candidates`, { params })
  return data
}

async function uploadToIntent(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', uploadUrl)
    request.setRequestHeader('Content-Type', 'application/pdf')
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100))
    })
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error('The file could not be uploaded to secure storage.'))
    })
    request.addEventListener('error', () =>
      reject(new Error('The file could not be uploaded to secure storage.')),
    )
    request.addEventListener('abort', () => reject(new Error('The upload was cancelled.')))
    request.send(file)
  })
}

export async function uploadCandidateDocument(
  candidateId: number,
  documentType: MobilityDocumentType,
  file: File,
  replacesId?: number,
  onProgress?: (progress: CandidateUploadProgress) => void,
): Promise<number> {
  onProgress?.({ phase: 'creating', percent: 0 })
  const path = replacesId
    ? `${BASE}/candidates/${candidateId}/documents/${replacesId}/replace-intent`
    : `${BASE}/candidates/${candidateId}/documents/upload-intent`
  const { data } = await apiClient.post<{ document_id: number; upload_url: string }>(path, {
    document_type: documentType,
    filename: file.name,
    mime_type: 'application/pdf',
    byte_size: file.size,
  })
  onProgress?.({ phase: 'uploading', percent: 0, documentId: data.document_id })
  await uploadToIntent(data.upload_url, file, (percent) =>
    onProgress?.({ phase: 'uploading', percent, documentId: data.document_id }),
  )
  onProgress?.({ phase: 'processing', percent: 100, documentId: data.document_id })
  await apiClient.post(`${BASE}/candidates/${candidateId}/documents/${data.document_id}/complete`)
  return data.document_id
}

export async function deleteCandidateDocument(candidateId: number, documentId: number) {
  await apiClient.delete(`${BASE}/candidates/${candidateId}/documents/${documentId}`)
}

export async function viewCandidateDocument(candidateId: number, documentId: number) {
  const { data } = await apiClient.get<{ download_url: string }>(
    `${BASE}/candidates/${candidateId}/documents/${documentId}/download`,
  )
  window.open(data.download_url, '_blank', 'noopener,noreferrer')
}

export async function getOrganizationChart(): Promise<OrganizationChart | null> {
  const { data } = await apiClient.get<OrganizationChart | null>(`${BASE}/organization-chart`)
  return data
}

export async function uploadOrganizationChart(file: File): Promise<string> {
  const { data } = await apiClient.post<{ import_id: string; upload_url: string }>(
    `${BASE}/organization-chart/imports/upload-intent`,
    { filename: file.name, mime_type: 'application/pdf', byte_size: file.size },
  )
  await uploadToIntent(data.upload_url, file)
  await apiClient.post(`${BASE}/organization-chart/imports/${data.import_id}/complete`)
  return data.import_id
}

export async function getOrganizationChartImport(importId: string): Promise<OrganizationChart> {
  const { data } = await apiClient.get<OrganizationChart>(
    `${BASE}/organization-chart/imports/${importId}`,
  )
  return data
}

export async function retryOrganizationChart(importId: string): Promise<void> {
  await apiClient.post(`${BASE}/organization-chart/imports/${importId}/retry`)
}

export async function patchOrganizationNode(
  importId: string,
  nodeId: string,
  revision: number,
  patch: Partial<ChartNode>,
): Promise<ChartNode> {
  const { data } = await apiClient.patch<ChartNode>(
    `${BASE}/organization-chart/imports/${importId}/nodes/${nodeId}`,
    { ...patch, expected_revision: revision },
  )
  return data
}

export async function createOrganizationNode(
  importId: string,
  revision: number,
  node: Partial<ChartNode> & Pick<ChartNode, 'node_type' | 'display_name'>,
): Promise<ChartNode> {
  const { data } = await apiClient.post<ChartNode>(
    `${BASE}/organization-chart/imports/${importId}/nodes`,
    { ...node, expected_revision: revision },
  )
  return data
}

export async function publishOrganizationChart(importId: string, revision: number) {
  const { data } = await apiClient.post<OrganizationChart>(
    `${BASE}/organization-chart/imports/${importId}/publish`,
    { expected_revision: revision },
  )
  return data
}

export async function getMobilityDepartments(): Promise<MobilityDepartment[]> {
  const { data } = await apiClient.get<MobilityDepartment[]>(
    `${BASE}/organization-chart/departments`,
  )
  return data
}

export async function assignMobilityJobDepartment(jobId: number, departmentId: string) {
  const { data } = await apiClient.patch<InternalJobPosting>(
    `${BASE}/internal-jobs/${jobId}/department`,
    {
      organization_department_id: departmentId,
    },
  )
  return data
}

export async function getMatrixLatest(params: {
  page: number
  limit: number
  search?: string
}): Promise<MatrixCandidateList> {
  const { data } = await apiClient.get<MatrixCandidateList>(`${BASE}/matrix/latest`, { params })
  return data
}

export async function getDepartmentTalentCoverage(params: {
  departmentPage: number
  departmentLimit: number
  candidatePage: number
  candidateLimit: number
  departmentSearch?: string
  coverageFilter?: DepartmentTalentCoverageFilter
  matrixClassification?: DepartmentTalentMatrixClassification
  strongMatchThreshold?: number
}): Promise<DepartmentTalentCoverage> {
  const {
    departmentPage,
    departmentLimit,
    candidatePage,
    candidateLimit,
    departmentSearch,
    coverageFilter,
    matrixClassification,
    strongMatchThreshold,
  } = params
  const { data } = await apiClient.get<DepartmentTalentCoverage>(
    `${BASE}/department-needs/talent-coverage`,
    {
      params: {
        department_page: departmentPage,
        department_limit: departmentLimit,
        candidate_page: candidatePage,
        candidate_limit: candidateLimit,
        department_search: departmentSearch || undefined,
        coverage_filter: coverageFilter,
        matrix_classification: matrixClassification,
        strong_match_threshold: strongMatchThreshold,
      },
    },
  )
  return data
}

export async function getInternalMobilityInsights(params?: {
  strongMatchThreshold?: number
  trainableCoverageThreshold?: number
}): Promise<DepartmentNeedsInsights> {
  const { data } = await apiClient.get<DepartmentNeedsInsights>(
    `${BASE}/department-needs/insights`,
    {
      params: {
        strong_match_threshold: params?.strongMatchThreshold,
        trainable_coverage_threshold: params?.trainableCoverageThreshold,
      },
    },
  )
  return data
}

export async function getInternalMobilityEvidenceQuality(): Promise<EvidenceQuality> {
  const { data } = await apiClient.get<EvidenceQuality>(`${BASE}/department-needs/evidence-quality`)
  return data
}

export async function getInternalMobilityReadiness(): Promise<InternalMobilityReadiness> {
  const { data } = await apiClient.get<DepartmentNeedsOverview>(`${BASE}/department-needs`, {
    params: { page: 1, limit: 1, include_empty: false },
  })
  return data.setup
}

export async function getDepartmentNeedRoleCandidates(params: {
  departmentId: string
  jobId: number
  page: number
  limit: number
  minScore?: number
}): Promise<DepartmentNeedCandidateList> {
  const { departmentId, jobId, minScore, ...pagination } = params
  const { data } = await apiClient.get<DepartmentNeedCandidateList>(
    `${BASE}/department-needs/${departmentId}/roles/${jobId}/candidates`,
    { params: { ...pagination, min_score: minScore } },
  )
  return data
}

export async function generateCandidate(candidateId: number): Promise<MatrixRun> {
  const { data } = await apiClient.post<MatrixRun>(
    `${BASE}/matrix/candidates/${candidateId}/generate`,
  )
  return data
}

export async function refreshChangedCandidates(): Promise<MatrixRun> {
  const { data } = await apiClient.post<MatrixRun>(`${BASE}/matrix/refresh-changed`)
  return data
}

export async function getMatrixRun(runId: string): Promise<MatrixRun> {
  const { data } = await apiClient.get<MatrixRun>(`${BASE}/matrix/runs/${runId}`)
  return data
}

export async function patchCandidateAnnotation(
  candidateId: number,
  patch: { review_status?: string; private_notes?: string },
) {
  const { data } = await apiClient.patch<{ review_status: string; private_notes: string }>(
    `${BASE}/matrix/candidates/${candidateId}/annotation`,
    patch,
  )
  return data
}
